/**
 * New-section defaults and one-line summaries for the composer list.
 *
 * A section added from the menu must be **immediately valid** against
 * `sectionsSchema` — otherwise Save fails on something the user never typed.
 * Every default here therefore satisfies its type's `min(1)` fields.
 */
import type { HomeSection, HomeSectionType } from "@flint/api-contract/store-home";

/** `crypto.randomUUID` is available in every browser the app supports. */
const id = () => crypto.randomUUID();

export function newSection(type: HomeSectionType): HomeSection {
  switch (type) {
    case "announcement_bar":
      return { id: id(), type, enabled: true, text: "Free delivery on orders over ₹50,000", tone: "info", dismissible: true };
    case "hero_slider":
      return {
        id: id(),
        type,
        enabled: true,
        slides: [{ headline: "Your headline here", ctas: [] }],
        autoplayMs: 0,
      };
    case "category_grid":
      return {
        id: id(),
        type,
        enabled: true,
        source: "collections",
        style: "cards",
        limit: 8,
        showCounts: true,
        heading: "Shop by collection",
      };
    case "product_grid":
      return {
        id: id(),
        type,
        enabled: true,
        source: { kind: "newest" },
        limit: 8,
        heading: "New this season",
      };
    case "collection_rail":
      return { id: id(), type, enabled: true, source: { kind: "all-visible" } };
    case "promo_banner":
      return { id: id(), type, enabled: true, headline: "Tell buyers something", imageSide: "right" };
    case "stats_strip":
      return { id: id(), type, enabled: true };
    case "about_block":
      return { id: id(), type, enabled: true };
    case "rich_text":
      return {
        id: id(),
        type,
        enabled: true,
        heading: "A heading",
        body: "Tell buyers something about your store.",
        align: "left",
      };
    case "image_text":
      return { id: id(), type, enabled: true, heading: "A heading", imageSide: "right" };
    case "testimonials":
      return {
        id: id(),
        type,
        enabled: true,
        heading: "What buyers say",
        items: [{ quote: "Great quality and reliable delivery.", author: "A happy dealer" }],
      };
    case "faq":
      return {
        id: id(),
        type,
        enabled: true,
        heading: "Wholesale FAQs",
        items: [{ question: "What is your minimum order?", answer: "Orders are placed by the pack." }],
      };
    case "logo_wall":
      return { id: id(), type, enabled: true, heading: "As seen at", items: [{ imageUrl: "" }], grayscale: true };
    case "gallery":
      return { id: id(), type, enabled: true, heading: "From our floor", images: ["", "", ""], columns: 3 };
    case "featured_product":
      return { id: id(), type, enabled: true, eyebrow: "Featured", headline: "Our bestseller this season" };
    case "contact":
      return { id: id(), type, enabled: true, heading: "Get in touch", showWhatsapp: true };
    case "newsletter":
      return { id: id(), type, enabled: true, heading: "Stay in the loop", subtext: "New drops and dealer offers, straight to your WhatsApp." };
    case "lead_capture":
      return { id: id(), type, enabled: true, heading: "Become a dealer", subtext: "Tell us about your business and we'll be in touch." };
  }
}

/** The muted second line in the section list — what this section will show. */
export function summarise(section: HomeSection): string {
  switch (section.type) {
    case "announcement_bar":
      return section.text;
    case "hero_slider": {
      const n = section.slides.length;
      const auto = section.autoplayMs > 0 ? ` · autoplay ${section.autoplayMs / 1000}s` : "";
      return `${n} slide${n === 1 ? "" : "s"}${auto}`;
    }
    case "category_grid":
      return `${section.style === "cards" ? "Cards" : "Circles"} · up to ${section.limit}`;
    case "product_grid": {
      const src =
        section.source.kind === "collection"
          ? section.source.slug
          : section.source.kind === "newest"
            ? "newest"
            : "not in a collection";
      return `${section.heading} · ${src} · ${section.limit}`;
    }
    case "collection_rail":
      return section.source.kind === "all-visible"
        ? "One rail per collection"
        : `Only ${section.source.slug}`;
    case "promo_banner":
      return section.headline;
    case "stats_strip":
      return section.items && section.items.length > 0
        ? section.items.map((s) => s.label).filter(Boolean).join(" · ") ||
            `${section.items.length} stat${section.items.length === 1 ? "" : "s"}`
        : "From your store profile";
    case "about_block":
      return section.body ? section.body.slice(0, 40) : "From your store profile";
    case "rich_text":
      return section.heading;
    case "image_text":
      return section.heading;
    case "testimonials":
      return `${section.items.length} quote${section.items.length === 1 ? "" : "s"}`;
    case "faq":
      return `${section.items.length} question${section.items.length === 1 ? "" : "s"}`;
    case "logo_wall":
      return `${section.items.length} logo${section.items.length === 1 ? "" : "s"}`;
    case "gallery":
      return `${section.images.length} image${section.images.length === 1 ? "" : "s"} · ${section.columns} columns`;
    case "featured_product":
      return section.headline;
    case "contact":
      return section.heading ?? "Contact block";
    case "newsletter":
      return section.heading;
    case "lead_capture":
      return section.heading;
  }
}
